import { createAsyncThunk } from "@reduxjs/toolkit";
import { jwtDecode } from "jwt-decode";

import { services } from "../../services";
import { type TUserAuthState } from "../../types/user.type";

const getUserLoginErrorMessage = (error: any) => {
	const errorCode = error?.response?.data?.error_code;
	const message =
		error?.response?.data?.msg ??
		error?.response?.data?.message ??
		error?.message;

	if (
		errorCode === "USER3" ||
		message === "User does not exist."
	) {
		return "Your account is authenticated, but it has not been set up in alloan.ai yet. Contact support.";
	}

	if (
		errorCode === "AUTH3" ||
		message === "Permission denied."
	) {
		return "Your account does not have access to this workspace.";
	}

	if (
		errorCode === "AUTH1" ||
		errorCode === "AUTH2" ||
		message ===
			"Missing authorization header for protected endpoint." ||
		message === "Token in authorization header is invalid."
	) {
		return "Your sign-in session could not be verified. Please try again.";
	}

	if (
		typeof message === "string" &&
		message.trim() &&
		message !== "[object Object]"
	) {
		return message;
	}

	return "We couldn't complete sign in. Please try again.";
};

// This thunk is responsible for handling password update.
export const passwordUpdatedThunk = createAsyncThunk(
	"auth/passwordUpdated",
	async (password: string) => {
		return await services.userServiceInstance.changePassword(
			password
		);
	}
);

// This thunk is responsible for handling user login. This is source of truth for user authentication.
export const userLoginThunk = createAsyncThunk(
	"auth/userLogin",
	async (_, { getState, rejectWithValue }) => {
		try {
			const data =
				await services.userServiceInstance.getUserMeta();
			const { user } = getState() as TUserAuthState;
			const decodedToken = jwtDecode(
				user?.token as string
			);
			data.isPasswordSet = (
				decodedToken as any
			).email_verified;
			return data;
		} catch (error: any) {
			return rejectWithValue(
				getUserLoginErrorMessage(error)
			);
		}
	}
);

export const getAllUsersInTenantThunk = createAsyncThunk(
	"auth/getAllUsersInTenant",
	async (_, { rejectWithValue }) => {
		try {
			const data =
				await services.userServiceInstance.getAllUsers();
			return data;
		} catch (error: any) {
			console.error("redux error", error);
			return rejectWithValue({
				...error.response.data,
				msg: `Error occurred: ${error.response.data.msg}`,
			});
		}
	}
);
